import { motion } from "framer-motion";
import { Scale, Megaphone, Users, FlaskConical, TrendingUp, ArrowRight, LucideIcon } from "lucide-react";

interface Practice {
  num: string;
  name: string;
  icon: LucideIcon;
  blurb: string;
  proof: string;
  anchor: string;
}

const practices: Practice[] = [
  {
    num: "01",
    name: "Lobbying",
    icon: Scale,
    blurb: "Bill monitoring, legislator engagement, and committee strategy across the Alabama State House.",
    proof: "See the live bill tracker",
    anchor: "lobbying",
  },
  {
    num: "02",
    name: "Strategic Communication",
    icon: Megaphone,
    blurb: "Narrative development, social listening, and earned media that move public opinion.",
    proof: "See live signal monitoring",
    anchor: "signal",
  },
  {
    num: "03",
    name: "Advocacy",
    icon: Users,
    blurb: "Coalition building and grassroots mobilization that turn pressure into policy wins.",
    proof: "See our coalition map",
    anchor: "partners",
  },
  {
    num: "04",
    name: "Research",
    icon: FlaskConical,
    blurb: "Policy research, county data analysis, and issue briefings that hold up under scrutiny.",
    proof: "See evidence we've pulled",
    anchor: "evidence",
  },
  {
    num: "05",
    name: "Economic Development",
    icon: TrendingUp,
    blurb: "Funding strategy and grant programs that move dollars into community-rooted work.",
    proof: "See the funding engine",
    anchor: "grants",
  },
];

const PracticeGrid = () => {
  return (
    <section id="practice" className="relative py-20 md:py-28 bg-background border-b border-border">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mb-12"
        >
          <span className="text-primary text-xs font-semibold tracking-[0.2em] uppercase">
            Our Practice
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mt-3 mb-4">
            Five disciplines, <span className="text-gradient-primary">one firm</span>
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Each practice is shown live on this site. Scroll down — or jump straight to the work product.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {practices.map((p, i) => (
            <motion.a
              key={p.name}
              href={`#${p.anchor}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.06 }}
              className="group rounded-[var(--radius)] border border-border bg-card/60 hover:bg-card hover:border-primary/40 transition-all p-6 flex flex-col"
            >
              <div className="flex items-center justify-between mb-5">
                <div className="w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center">
                  <p.icon className="h-5 w-5 text-primary" />
                </div>
                <span className="font-display text-xs font-semibold text-muted-foreground tracking-[0.2em]">
                  {p.num}
                </span>
              </div>
              <h3 className="font-display text-lg font-bold mb-2 group-hover:text-primary transition-colors">
                {p.name}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5 flex-1">
                {p.blurb}
              </p>
              <span className="inline-flex items-center gap-1 text-xs text-primary font-semibold">
                {p.proof}
                <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
              </span>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PracticeGrid;